import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"
import { connect } from "react-redux"
import {
  Button, Modal, ModalBody, ModalFooter, ModalHeader,
  Nav, NavItem, NavLink, TabContent, TabPane, Table
} from "reactstrap"
import { withTranslation } from "react-i18next"
import classnames from "classnames"
import moment from "moment"
import { isEmpty, map } from "lodash"
import Skeleton from "react-loading-skeleton"
import { buscarDispositivoLogs } from "store/auth/profile/actions"
import { apiBuscarNotificacoes, apiBuscarNotificacoesUsuario } from "../../helpers/backend_helper"
import { Filters } from "../../common/data/definitions"

const describeDateTime = (d, t) => d
  ? moment(d).format("DD/MM/YY HH:mm")
  : t("cardapio.dispositivo.logsnotif.tipo.nd")

const describeType = (tipo, t) => {
  switch (tipo) {
    case "featured":    return t("cardapio.dispositivo.logsnotif.tipo.featured")
    case "products":    return t("cardapio.dispositivo.logsnotif.tipo.products")
    case "app_version": return t("cardapio.dispositivo.logsnotif.tipo.appversion")
    default:            return tipo
      ? t("cardapio.dispositivo.logsnotif.tipo.outro")
      : t("cardapio.dispositivo.logsnotif.tipo.nd")
  }
}

const DispositivoModal = props => {
  const { t, isOpen, dispositivo, onClose, dispositivoLogs, dispositivoLogsLoading } = props
  const [tab, setTab] = useState("logs")
  const [notificacoes, setNotificacoes] = useState([])
  const [notificacoesLoading, setNotificacoesLoading] = useState(false)
  const refreshRef = useRef(null)

  const buscarLogs = () => {
    if (dispositivo?.id) props.buscarDispositivoLogs({ dispositivoId: dispositivo.id })
  }

  const buscarNotificacoesDoDispositivo = () => {
    if (!dispositivo?.id) return
    setNotificacoesLoading(true)
    try {
      const filters = [{ id: "dispositivos_ids", value: dispositivo.id, condition: Filters.arrIncludes }, { id: "notifica_baixa_bateria", value: true }]
      if (dispositivo.idEmpresa) {
        filters.push({ id: "idEmpresa", value: [dispositivo.idEmpresa] })
      }
      apiBuscarNotificacoesUsuario({ filters: JSON.stringify(filters), projection: JSON.stringify(['id', 'created_at', 'notificacao.titulo', 'usuario.nome_completo', 'lido', 'data_lida', 'tipo_notificacao']), sorting: "[{\"id\": \"created_at\", \"desc\":true}]" }).then(res => {
        setNotificacoes(res?.data || [])
      })
    } catch (e) {
      console.error(e)
      setNotificacoes([])
    } finally {
      setNotificacoesLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      buscarLogs()
      buscarNotificacoesDoDispositivo()
      refreshRef.current = setInterval(buscarLogs, 10000)
    }
    return () => {
      if (refreshRef.current) {
        clearInterval(refreshRef.current)
        refreshRef.current = null
      }
    }
  }, [isOpen, dispositivo?.id])

  const skeletonRow = i => (
    <tr key={ `sk-${i}` }>
      <td><Skeleton /></td><td><Skeleton /></td><td><Skeleton /></td><td><Skeleton /></td>
    </tr>
  )

  const describeType = tipo => {
    switch (tipo) {
      case "S":
        return "Sistema"
      case "P":
        return "Push Web"
      case "E":
        return "E-mail"
      default:
        return "N/D"
    }
  }

  return (
    <Modal isOpen={ isOpen } toggle={ onClose } size="xl" centered scrollable>
      <ModalHeader toggle={ onClose }>
        { t("cardapio.dispositivo.logsnotif.titulo") }
      </ModalHeader>
      <ModalBody>
        <Nav tabs>
          <NavItem>
            <NavLink className={ classnames({ active: tab === "logs" }) }
                     onClick={ () => setTab("logs") } style={ { cursor: "pointer" } }>
              { t("cardapio.dispositivo.logsnotif.tab.logs") }
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink className={ classnames({ active: tab === "notificacoes" }) }
                     onClick={ () => setTab("notificacoes") } style={ { cursor: "pointer" } }>
              { t("cardapio.dispositivo.logsnotif.tab.notificacoes") }
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={ tab } className="p-3">
          <TabPane tabId="logs">
            <div className="table-responsive">
              <Table className="table table-centered table-nowrap">
                <thead>
                  <tr>
                    <th>{ t("cardapio.dispositivo.logsnotif.logs.coluna.codigo") }</th>
                    <th>{ t("cardapio.dispositivo.logsnotif.logs.coluna.data") }</th>
                    <th>{ t("cardapio.dispositivo.logsnotif.logs.coluna.tipo") }</th>
                    <th>{ t("cardapio.dispositivo.logsnotif.logs.coluna.detalhes") }</th>
                  </tr>
                </thead>
                <tbody>
                  { dispositivoLogsLoading
                    ? [0,1,2,3,4].map(skeletonRow)
                    : isEmpty(dispositivoLogs)
                      ? (<tr><td colSpan="4">{ t("cardapio.dispositivo.logsnotif.logs.vazio") }</td></tr>)
                      : map(dispositivoLogs, (item, i) => (
                          <tr key={ i }>
                            <td>{ item.id }</td>
                            <td>{ describeDateTime(item.ultimaAtualizacao, t) }</td>
                            <td>{ describeType(item.tipo, t) }</td>
                            <td>
                              { item.erro ? t("cardapio.dispositivo.logsnotif.detalhes.erroprefixo") : "" }
                              { item.erroMessage }
                            </td>
                          </tr>
                      )) }
                </tbody>
              </Table>
            </div>
          </TabPane>
          <TabPane tabId="notificacoes">
            <div className="table-responsive">
              <Table className="table table-centered table-nowrap">
                <thead>
                  <tr>
                    <th>{ t("cardapio.dispositivo.logsnotif.notif.coluna.codigo") }</th>
                    <th>{ t("cardapio.dispositivo.logsnotif.notif.coluna.data") }</th>
                    <th>{ t("cardapio.dispositivo.logsnotif.notif.coluna.tipo") }</th>
                    <th>{ t("cardapio.dispositivo.logsnotif.notif.coluna.titulo") }</th>
                    <th>{ t("cardapio.dispositivo.logsnotif.notif.coluna.usuario") }</th>
                  </tr>
                </thead>
                <tbody>
                  { notificacoesLoading
                    ? [0,1,2,3].map(skeletonRow)
                    : isEmpty(notificacoes)
                      ? (<tr><td colSpan="4">{ t("cardapio.dispositivo.logsnotif.notif.vazio") }</td></tr>)
                      : map(notificacoes, (n, i) => (
                          <tr key={ i }>
                            <td>{ n.id }</td>
                            <td>{ describeDateTime(n.created_at || n.data, t) }</td>
                            <td>{ describeType(n.tipo_notificacao)}</td>
                            <td>{ n.notificacao.titulo }</td>
                            <td>{ n.usuario.nome_completo }</td>
                          </tr>
                      )) }
                </tbody>
              </Table>
            </div>
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={ onClose }>
          { t("cardapio.dispositivo.logsnotif.botao.fechar") }
        </Button>
      </ModalFooter>
    </Modal>
  )
}

DispositivoModal.propTypes = {
  t: PropTypes.func,
  isOpen: PropTypes.bool.isRequired,
  dispositivo: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  dispositivoLogs: PropTypes.array,
  dispositivoLogsLoading: PropTypes.bool,
  buscarDispositivoLogs: PropTypes.func
}

const mapStateToProps = state => {
  const { dispositivoLogs, dispositivoLogsLoading } = state.Profile
  return { dispositivoLogs, dispositivoLogsLoading }
}

export default withTranslation()(
  connect(mapStateToProps, { buscarDispositivoLogs })(DispositivoModal)
)
